import { getOrderDateRange } from 'checkout/Components/Confirmation/BookingDetailsV2/utils'
import { getItineraryCountries } from 'checkout/lib/utils/cruises/itinerary'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { findByName, findByTwoLetterCode, isValidInsuranceCountry } from 'lib/insurance/insuranceCountries'
import getObjectKey from 'lib/object/getObjectKey'
import getTourV2CountryCodesVisited from 'lib/tours/getTourV2CountryCodesVisited'
import moment from 'moment'
import qs from 'qs'

const insuranceTypeMap: Record<App.InsuranceType, string> = {
  travel_protection: 'protection',
  insurance: 'insurance',
  cfmr: 'protection',

}

export function getInsuranceTypeFromOrder(insuranceItem: App.OrderInsuranceItem) {
  if (!insuranceItem.insuranceType) return 'insurance'
  return insuranceTypeMap[insuranceItem.insuranceType]
}

const cancellationProtectionPolicyIds = new Set<App.InsurancePolicyId>([
  'travel_flight_cancellation_cover',
  'comprehensive_travel_insurance',
])

export function orderHasCancellationProtection(order: App.Order): boolean {
  const insuranceItem = order.insuranceItems.find(item => item.status === 'completed')
  if (insuranceItem) {
    return insuranceItem.policyIds.some(id => cancellationProtectionPolicyIds.has(id))
  }
  return false
}

/**
 * This is for actual cancellation protection, as opposed to the above 'orderHasCancellationProtection'
 * which refers to insurance with cancellation protection as a subset
 **/
export function orderHasBookingProtection(order: App.Order): boolean {
  return !!order.bookingProtectionItems.find(item => item.status === 'completed')
}

/**
 * Finds the active refund protection item on the order given if avaialble
 * Note: Refund protect is a specific 'booking protection' product
 * @param order The order to look for
 * @returns The booking protection item or nothing if not applicable
 */
export function getRefundProtectOrderItem(order: App.Order): App.OrderBookingProtectionItem | undefined {
  return order.bookingProtectionItems.find(item => item.status === 'completed')
}

/**
 * Finds the active booking protection insurance item on the order given if avaialble
 * Note: booking protection is a specific 'insurance' product
 * @param order The order to look for
 * @returns The insurance item or nothing if not applicable
 */
export function getBookingProtectionInsuranceOrderItem(order: App.Order): App.OrderInsuranceItem | undefined {
  return order.insuranceItems.find(item => item.status === 'completed' && item.insuranceType === 'cfmr')
}

export function getXCoverClaimUrl(item: App.OrderInsuranceItem) {
  const query = qs.stringify({ bookingID: item.idProduct })
  return `https://www.xcover.com/en/claims/?${query}`
}

export function getInsuranceDatesFromOrder(order: App.Order) {
  const dates = getOrderDateRange(order)
  if (!dates.earliestDate || !dates.latestDate) return
  return {
    startDate: moment(dates.earliestDate.toISOString()).format(ISO_DATE_FORMAT),
    endDate: moment(dates.latestDate.toISOString()).format(ISO_DATE_FORMAT),
  }
}

export function getInsuranceDestinationCountriesFromOrder(
  order: App.Order,
  cruiseOfferSnapshots: Record<string, App.Cruises.CruiseOfferSnapshot> = {},
  offers: Record<string, App.AnyOffer> = {},
  flightDetailsByOrderItems: Record<string, App.FlightOrderItemDetails> = {},
  experienceOffers: Record<string, App.ExperienceOffer> = {},
): Array<string> {
  if (order.items.length) {
    const cruiseV1Offer = order.items
      .map(item => offers[item.offer.id] as App.TourOffer)
      .find(offer => offer?.holidayTypes?.includes('Cruises'))

    if (cruiseV1Offer) {
      const locations = cruiseV1Offer.locationsVisited?.map(location => location.country) || []
      return nonNullable(unique(locations))
    }

    return nonNullable(order.items.map(item =>
      item.offer.property?.geoData?.country ||
      // In some cases, the geodata snapshot is not available at the order level
      // Therefore, we retrieve it directly from the offer that was fetched during checkout loading
      (offers[item.offerId] as App.HotelOffer)?.property?.geoData.country))
  } else if (order.bedbankItems.length) {
    return order.bedbankItems.map(item => item.offer.property.address.countryName)
  } else if (order.tourItems.length) {
    const completedItems = order.tourItems.filter(item => item.status === 'completed')
    const countryCodes = unique(completedItems.flatMap(item => {
      const tourOffer = offers[item.tourId] as App.Tours.TourV2Offer
      if (!tourOffer) return []
      const variation = Object.values(tourOffer.variations).find(variation => variation.fkTourOptionId === item.tourOptionId)
      if (!variation) return []
      return getTourV2CountryCodesVisited(variation.itinerary)
    }))
    if (countryCodes.length) {
      return nonNullable(countryCodes.map(code => findByTwoLetterCode(code)))
    }
    return unique(nonNullable(completedItems.flatMap(item => item.tour.countriesVisited.map(country => findByName(country)?.name))))
  } else if (order.cruiseItems.length) {
    const bookingIds = unique(order.cruiseItems.map(item => item.bookingId))

    const countries = bookingIds.flatMap(bookingId => {
      const offer = cruiseOfferSnapshots[bookingId]
      if (offer) {
        return getItineraryCountries(offer)
      }
    }).filter(isValidInsuranceCountry)

    return nonNullable(countries)
  } else if (order.flightItems.length) {
    if (!flightDetailsByOrderItems || !Object.keys(flightDetailsByOrderItems).length) return []

    const flightItemsIds = order.flightItems.map(item => item.id)

    const destinationCountries = flightItemsIds.reduce<Array<string | undefined>>((acc, curr) => {
      const currentFlightDetails: App.FlightOrderItemDetails | undefined = flightDetailsByOrderItems[curr]
      const countryCodes = currentFlightDetails?.bookingJourney.legs.map(leg => findByTwoLetterCode(leg.arrivalCountryCode)) || []
      return [...acc, ...countryCodes]
    }, [])

    return nonNullable(destinationCountries)
  } else if (order.experienceItems.length) {
    const experienceIds = Object.keys(experienceOffers)

    const destinationCountryCodes = experienceIds.flatMap((experienceId) => {
      const offer: App.ExperienceOffer | undefined = experienceOffers[experienceId]
      if (!offer) return

      return offer.redemptionLocations.map(location => findByTwoLetterCode(location.countryCode))
    })

    return nonNullable(destinationCountryCodes)
  }

  return []
}

interface UpdateQuoteParams {
  startDate: string;
  endDate: string;
  travellers: Array<App.OrderInsuranceItemTraveller>;
  itemId: string;
  coverageAmount: number;
}

export function getOrderInsuranceUpdateQuoteKey(params: UpdateQuoteParams) {
  return getObjectKey({
    startDate: params.startDate,
    endDate: params.endDate,
    travellers: params.travellers,
    itemId: params.itemId,
    coverageAmount: params.coverageAmount,
  })
}

export function orderHasInsuranceBookingProtection(order: App.Order) {
  return order.insuranceItems.some(item => item.insuranceType === 'cfmr')
}
